import { Box, Typography } from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { ExchangeBlock } from '@/apps/latest/features/knowledge/ExchangeBlock'
import { QuestionInput } from '@/apps/latest/features/knowledge/QuestionInput'
import { Layout } from '@/apps/latest/components/templates/Layout'
import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import {
  failedConversationId,
  useAskKnowledgeQuestionMutation,
  useKnowledgeConversationQuery,
  useStartKnowledgeConversationMutation,
  type KnowledgeMessage,
} from '@/features/internal/knowledge'
import { scaledFontSize } from '@/shared/styles/typography'

// 「マニュアル」だけでは点数表・疑義解釈まで引けることが読めないため、初出 1 行で範囲を定義する。
const SCOPE_DESCRIPTION =
  '調剤報酬の点数表・疑義解釈と、社内マニュアルをもとに答えます。'

const PLACEHOLDER = '算定要件や社内ルールについて質問する'

// 生成中に見せる仮の質問。サーバの往復が返るまで画面から質問が消えないようにする。
const pendingExchange = (question: string): KnowledgeMessage => ({
  id: 'pending',
  role: 'user',
  content: question,
  citations: [],
  created_at: '',
})

export const KnowledgePage = () => {
  const navigate = useNavigate()
  const { conversationId } = useParams<{ conversationId?: string }>()
  // 送信した質問と、送信時点で記録済みだった発言数。数を持つのは、取り直しが届いたか
  // どうかを本文の一致ではなく件数で判定するため — 同じ質問をもう一度送る導線
  // (失敗時の案内) があるので、本文で照合すると再送が仮の質問として出なくなる。
  const [pending, setPending] = useState<{
    question: string
    recordedCount: number
  } | null>(null)
  const bottomRef = useRef<HTMLDivElement>(null)

  const { data: conversation } = useKnowledgeConversationQuery(conversationId)
  const startConversation = useStartKnowledgeConversationMutation()
  const askQuestion = useAskKnowledgeQuestionMutation(conversationId ?? '')

  const isAnswering = startConversation.isPending || askQuestion.isPending
  const messages = conversation?.messages ?? []

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
  }, [messages.length, pending])

  const handleSubmit = (question: string) => {
    setPending({ question, recordedCount: messages.length })
    if (!conversationId) {
      startConversation.mutate(question, {
        onSuccess: (result) => navigate(`/knowledge/${result.conversation_id}`),
        // 失敗しても back は質問を記録する。その会話へ移さないと、利用者からは
        // 質問が消えたのにサイドバーの履歴にだけ残る。
        onError: (error) => {
          const failed = failedConversationId(error)
          if (failed) navigate(`/knowledge/${failed}`)
        },
        onSettled: () => setPending(null),
      })
      return
    }
    askQuestion.mutate(question, { onSettled: () => setPending(null) })
  }

  // messages は [質問, 回答, 質問, 回答, …] の順で返る。往復に畳んで描く。
  // **記録済みで回答が無い往復は失敗**。生成に失敗しても back は質問を残すため、
  // 待ちと区別しないと終わった往復に「資料を確認中…」が永久に出る。
  const exchanges: {
    question: KnowledgeMessage
    answer?: KnowledgeMessage
    isPending: boolean
  }[] = []
  messages.forEach((message) => {
    if (message.role === 'user') {
      exchanges.push({ question: message, isPending: false })
      return
    }
    const last = exchanges[exchanges.length - 1]
    if (last && !last.answer) last.answer = message
  })
  // 仮の質問は、その往復がまだ記録に現れていない間だけ出す。取り直しは仮の質問を
  // 片付けるより先に届く (mutation は callback の promise を待ってから settle する) ので、
  // 件数で見ないと同じ質問が一瞬 2 度並ぶ。
  if (pending && messages.length <= pending.recordedCount) {
    exchanges.push({
      question: pendingExchange(pending.question),
      isPending: true,
    })
  }

  return (
    <Layout breadcrumb={['マニュアル']}>
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ flex: 1 }}>
          {exchanges.length === 0 ? (
            <Typography
              component="p"
              sx={{
                fontSize: scaledFontSize('bodySm'),
                color: colorTokens.fg.muted,
                mb: 2,
              }}
            >
              {SCOPE_DESCRIPTION}
            </Typography>
          ) : (
            exchanges.map((exchange) => (
              <ExchangeBlock
                key={exchange.question.id}
                question={exchange.question}
                answer={exchange.answer}
                isPending={exchange.isPending}
              />
            ))
          )}
          <div ref={bottomRef} />
        </Box>

        {/* 長い往復でも入力欄に届くよう最下部へ貼る。地色を敷かないと本文が透ける。 */}
        <Box
          sx={{
            position: 'sticky',
            bottom: 0,
            pt: 2,
            pb: 1,
            backgroundColor: colorTokens.bg.default,
          }}
        >
          <QuestionInput
            disabled={isAnswering}
            placeholder={PLACEHOLDER}
            onSubmit={handleSubmit}
          />
        </Box>
      </Box>
    </Layout>
  )
}

export default KnowledgePage
